import { stockLevel } from '@pl/domain';
import type { StreamEventDto, WallAlertDto, WallOrderDto, WallSiteDto, WallSkuDto, WallSnapshotDto, WallTransferDto } from '@pl/domain';
import { Fragment, useMemo, type ReactNode } from 'react';
import { describeAction, type LinePart } from '../lib/describe';
import { Swatch, shortName } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { useFitRows, useFitTiles, useFontsLoaded } from './fit';
import { useNightlyReload, useNow, useWallData } from './useWallData';

export const HIDDEN_LINES = 'Some lines hidden during a count';
const boxes = (n: number) => `${n} ${n === 1 ? 'box' : 'boxes'}`;
/**
 * What a board may print for an order's or a transfer's totals: none while a count hides one of its
 * lines. The server sends null then; `linesHidden` alone is enough here too, so a sum of the lines
 * left is never printed as the whole ("0 boxes" for a draft of 5). The Wall prints "—" instead.
 */
const whole = <T,>(x: { linesHidden: boolean }, totals: T | null): T | null => (x.linesHidden ? null : totals);
/** "5 boxes", or "— boxes" when the transfer's totals are not the Wall's to print. */
// A figure the server withheld (a whole-totals null, or a count-hidden `picked`) is drawn as "—", never as 0.
const boxesOf = (x: WallTransferDto, n: (t: NonNullable<WallTransferDto['totals']>) => number | null) => {
  const totals = whole(x, x.totals);
  const v = totals ? n(totals) : null;
  return v === null ? '— boxes' : boxes(v);
};
/** A figure as the board draws it, with a true minus sign. */
const figure = (n: number) => String(n).replace('-', '−');
/**
 * The size class for a big figure by how many characters it has: "38" and "105" at full size, but
 * a backorder's "−105" or a "−9999" drawn smaller, so it stays in its tile or its column.
 */
const wide = (text: string) => (text.length >= 5 ? 'w5' : text.length === 4 ? 'w4' : undefined);
/** Transfers listed under the lead one, at most. The panel's header gives the whole number. */
const MORE_TRANSFERS = 3;
/** The lead transfer's SKU lines shown, at most. */
const LEAD_LINES = 3;

/**
 * The last row of a list that could not show everything: how many it left out. While the list is
 * being measured it is drawn anyway, as the `data-probe` row whose height is measured.
 */
function MoreRow({ fit, total, as: Tag = 'li' }: { fit: { shown: number; measuring: boolean }; total: number; as?: 'li' | 'div' }) {
  if (fit.measuring) {
    return (
      <Tag className="w-more" data-probe="">
        +{total} more
      </Tag>
    );
  }
  return fit.shown < total ? <Tag className="w-more">+{total - fit.shown} more</Tag> : null;
}

/**
 * One formatter per zone and set of options, made once: a TV's processor is slow, and making an
 * `Intl.DateTimeFormat` costs far more than using one. A zone this browser does not know is refused
 * when the formatter is made, and falls back to UTC. (Kept per `DateTimeFormat` constructor, so a
 * different one — a test's — starts afresh.)
 */
let formatters: { of: typeof Intl.DateTimeFormat; byKey: Map<string, Intl.DateTimeFormat> } | null = null;
function formatter(timeZone: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  if (formatters?.of !== Intl.DateTimeFormat) formatters = { of: Intl.DateTimeFormat, byKey: new Map() };
  const key = `${timeZone}|${JSON.stringify(opts)}`;
  let f = formatters.byKey.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone });
    } catch {
      f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' });
    }
    formatters.byKey.set(key, f);
  }
  return f;
}

/**
 * Formats in the site's zone. Below the Chrome 57 floor there is no `formatToParts`, and the whole
 * string comes back as one part rather than the board failing to draw.
 */
function fmt(at: number | string, timeZone: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormatPart[] {
  const d = typeof at === 'number' ? at : Date.parse(at);
  const f = formatter(timeZone, opts);
  return typeof f.formatToParts === 'function' ? f.formatToParts(d) : [{ type: 'literal', value: f.format(d) }];
}
const joined = (parts: Intl.DateTimeFormatPart[]) => parts.map((p) => p.value).join('');
const part = (parts: Intl.DateTimeFormatPart[], type: string) => parts.find((p) => p.type === type)?.value ?? '';

/** "2:32:08", a scan's time on the site's clock. */
const scanTime = (iso: string, tz: string) => {
  const p = fmt(iso, tz, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  return `${part(p, 'hour')}:${part(p, 'minute')}:${part(p, 'second')}`;
};
/** "2:31 PM". */
const shortTime = (at: number | string, tz: string) => joined(fmt(at, tz, { hour: 'numeric', minute: '2-digit' }));

const skuWords = (sku: Pick<WallSkuDto, 'name' | 'colourName'> | undefined) => (sku ? [shortName(sku.name), sku.colourName].filter(Boolean).join(' ') : 'Unknown SKU');

const CLOCK: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
const DATE: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' };

/** The header clock: the one thing on the board that changes every second, so it ticks on its own. */
function WallClock({ tz }: { tz: string }) {
  const clock = fmt(useNow(1000), tz, CLOCK);
  return (
    <div className="w-clock">
      <b>
        {part(clock, 'hour')}:{part(clock, 'minute')}
      </b>
      <span>
        {part(clock, 'dayPeriod')} {part(clock, 'timeZoneName')}
      </span>
    </div>
  );
}

/** "Tuesday, September 22", which changes at midnight: looked at once a minute. */
function WallDate({ tz }: { tz: string }) {
  return <div className="w-date">{joined(fmt(useNow(60_000), tz, DATE))}</div>;
}

function SiteCode({ site }: { site: WallSiteDto | undefined }) {
  return <span className="stag">{site?.shortCode ?? '?'}</span>;
}

/** An event's words, as the Wall draws them: a swatch with each SKU, the order in mono. */
function Words({ parts }: { parts: LinePart[] }) {
  return (
    <>
      {parts.map((p, i) => {
        if (p.kind === 'sku')
          return (
            <Fragment key={i}>
              <Swatch hex={p.sku?.colourHex ?? null} name={p.sku?.colourName ?? null} />
              {skuWords(p.sku)}
            </Fragment>
          );
        if (p.kind === 'ref')
          return (
            <span key={i} className="mono">
              {p.text}
            </span>
          );
        const t = p.text.trim();
        if (!t) return null;
        return ['→', 'for', 'on', 'from'].includes(t) ? <em key={i}>{t}</em> : <span key={i} className="verb">{t}</span>;
      })}
    </>
  );
}

function ScanRow({ e, first, skus, tz }: { e: StreamEventDto; first: boolean; skus: Map<string, WallSkuDto>; tz: string }) {
  const rejected = e.type === 'scan.rejected';
  const cls = rejected ? 'bad' : first ? 'new' : undefined;
  let what: ReactNode;
  if (rejected && e.payload.reasonCode === 'WRONG_SKU') {
    const sku = e.skuId ? skus.get(e.skuId) : undefined;
    what = (
      <span className="what">
        <span className="tag">Wrong color</span>
        <span className="line2">
          <Swatch hex={sku?.colourHex ?? null} name={sku?.colourName ?? null} />
          {skuWords(sku)}
          {e.orderRef && (
            <>
              <em>for</em>
              <span className="mono">{e.orderRef}</span>
            </>
          )}
        </span>
      </span>
    );
  } else if (rejected) {
    what = (
      <span className="what">
        <span className="tag">Refused</span>
        {/* `msg`: the whole message where it fits, two lines at most, then an ellipsis. */}
        <span className="line2 msg">{typeof e.payload.message === 'string' ? e.payload.message : 'The scan was refused'}</span>
      </span>
    );
  } else {
    what = (
      <span className="what">
        <Words parts={describeAction(e, skus)} />
      </span>
    );
  }
  return (
    <li className={cls}>
      <time dateTime={e.at}>{scanTime(e.at, tz)}</time>
      <span className="who">{e.actorName ?? ''}</span>
      {what}
    </li>
  );
}

function OrderRow({ o }: { o: WallOrderDto }) {
  // A board missing lines is not a complete order: it has no totals to print ("—", and no bar), and
  // it never reads "Ready to confirm".
  const known = whole(o, o.totalQty !== null && o.scanned !== null ? { total: o.totalQty, scanned: o.scanned } : null);
  const ready = known !== null && known.total > 0 && known.scanned === known.total;
  const pct = known && known.total > 0 ? Math.min(100, (known.scanned / known.total) * 100) : 0;
  return (
    <li className={ready ? 'ready' : !known ? 'unknown' : known.scanned === 0 ? 'idle' : undefined}>
      <span className="o-code">{o.externalRef ?? o.code}</span>
      {ready ? (
        <span className="o-sub">
          <Icon name="check" size={28} />
          Ready to confirm
        </span>
      ) : o.linesHidden ? (
        <span className="o-sub w-hidden">{HIDDEN_LINES}</span>
      ) : (
        <span className="o-sub">{o.customerName}</span>
      )}
      <span className="o-nm">
        {known ? (
          <>
            {known.scanned}
            <i>/{known.total}</i>
          </>
        ) : (
          '—'
        )}
      </span>
      {known && (
        <div className={ready ? 'w-bar ok' : 'w-bar'}>
          <i style={{ width: `${pct}%` }} />
        </div>
      )}
    </li>
  );
}

function Transfers({
  out,
  into,
  sites,
  skus,
  tz,
  hereId,
  fonts,
}: {
  out: WallTransferDto[];
  into: WallTransferDto[];
  sites: Map<string, WallSiteDto>;
  skus: Map<string, WallSkuDto>;
  tz: string;
  hereId: string;
  fonts: number;
}) {
  const onRoad = out.filter((x) => x.status === 'in_transit');
  const lead = onRoad[0];
  const rest = out.filter((x) => x !== lead);
  const far = (x: WallTransferDto) => sites.get(x.fromSiteId === hereId ? x.toSiteId : x.fromSiteId);
  // Incoming first: those are the boxes this site has to receive.
  const more = [...into, ...rest];
  const fit = useFitRows<HTMLDivElement>(Math.min(more.length, MORE_TRANSFERS), [out, into, fonts], more.length);
  const listed = more.slice(0, fit.shown);
  if (!lead && more.length === 0) return <div className="w-empty">No transfers in play</div>;
  const leadTotals = lead ? whole(lead, lead.totals) : null;
  // The note that lines are hidden takes a line's place: the lead's block is never taller during a
  // count than without one, so the transfers under it keep their rows.
  const leadLines = lead ? LEAD_LINES - (lead.linesHidden ? 1 : 0) : 0;
  return (
    <div className="xfer">
      {lead && (
        <>
          <div className="xf-main">
            <div className="big">
              {leadTotals ? leadTotals.sent : '—'}
              <small>{leadTotals?.sent === 1 ? 'box' : 'boxes'}</small>
            </div>
            <div>
              <div className="xf-id">
                <span className="mono">{lead.code}</span>
                <Icon name="arrow-right" size={30} />
                <SiteCode site={far(lead)} />
              </div>
              <div className="xf-sub">
                {lead.sentAt && `Sent ${shortTime(lead.sentAt, tz)}${lead.sentByName ? ` · ${lead.sentByName}` : ''}`}
                <br />
                In transit{leadTotals && ` · ${far(lead)?.shortCode ?? '?'} has ${leadTotals.received} of ${leadTotals.sent}`}
              </div>
            </div>
          </div>
          <div className="xf-lines">
            {lead.lines.slice(0, leadLines).map((l) => {
              const sku = skus.get(l.skuId);
              return (
                <div key={l.skuId}>
                  <Swatch hex={sku?.colourHex ?? null} name={sku?.colourName ?? null} />
                  {skuWords(sku)}
                  <b>{l.sent}</b>
                </div>
              );
            })}
            {lead.lines.length > leadLines && <div className="w-more">+{lead.lines.length - leadLines} more</div>}
            {lead.linesHidden && <div className="w-hidden">{HIDDEN_LINES}</div>}
          </div>
        </>
      )}
      {more.length > 0 && (
        <div className="xf-more" ref={fit.ref}>
          {listed.map((x) => (
            <div key={x.id}>
              <span className="mono">{x.code}</span>
              {x.toSiteId === hereId ? (
                <>
                  {boxesOf(x, (t) => t.stillInTransit)} from <SiteCode site={far(x)} />
                </>
              ) : (
                <>
                  → <SiteCode site={far(x)} />· {x.status === 'draft' ? `draft, ${boxesOf(x, (t) => t.picked)}` : `in transit, ${boxesOf(x, (t) => t.sent)}`}
                </>
              )}
              {x.linesHidden && <span className="w-hidden xf-hidden">{HIDDEN_LINES}</span>}
            </div>
          ))}
          <MoreRow fit={fit} total={more.length} as="div" />
        </div>
      )}
    </div>
  );
}

function AlertRow({ a, skus, sites, hereId }: { a: WallAlertDto; skus: Map<string, WallSkuDto>; sites: Map<string, WallSiteDto>; hereId: string }) {
  const sku = a.skuId ? skus.get(a.skuId) : undefined;
  return (
    <li className={a.level}>
      <span className={['n', wide(figure(a.value))].filter(Boolean).join(' ')}>{figure(a.value)}</span>
      <div>
        <div className="t">
          {a.siteId !== hereId && <SiteCode site={sites.get(a.siteId)} />}
          {sku ? (
            <>
              <Swatch hex={sku.colourHex} name={sku.colourName} />
              {skuWords(sku)}
            </>
          ) : (
            'Rejected scans'
          )}
        </div>
        <div className="s">{a.message}</div>
      </div>
    </li>
  );
}

/** "CAT6 CMR", from the SKU's type and jacket: riser and plenum cable are different products. */
function groupOf(sku: WallSkuDto): string {
  const type = typeof sku.attributes.type === 'string' ? sku.attributes.type : null;
  const jacket = typeof sku.attributes.jacket === 'string' ? sku.attributes.jacket : null;
  if (type && jacket) return `${type} ${jacket}`;
  return type ?? shortName(sku.name);
}

function AvailableNow({ s, fonts }: { s: WallSnapshotDto; fonts: number }) {
  const groups = useMemo(() => {
    const counting = new Set(s.countingSkuIds);
    const rows = new Map(s.stock.filter((r) => r.siteId === s.site.id).map((r) => [r.skuId, r]));
    const out = new Map<string, { sku: WallSkuDto; available: number | null }[]>();
    for (const sku of s.skus) {
      if (!counting.has(sku.id) && !rows.has(sku.id)) continue;
      const g = groupOf(sku);
      const list = out.get(g) ?? [];
      list.push({ sku, available: counting.has(sku.id) ? null : rows.get(sku.id)!.available });
      out.set(g, list);
    }
    return [...out.entries()];
  }, [s]);
  const fit = useFitTiles<HTMLElement>(
    groups.map(([, tiles]) => tiles.length),
    [groups, fonts],
  );
  // SKUs in groups left off the end, when not even a "+N more" for each group fits.
  const offTheEnd = groups.slice(fit.groups).reduce((n, [, tiles]) => n + tiles.length, 0);
  return (
    <section className="w-panel stock" aria-label="Available now" ref={fit.ref}>
      <div className="st-title">
        <span className="w-k">
          Available
          <br />
          now
        </span>
        <span className="note">boxes</span>
      </div>
      {groups.slice(0, fit.groups).map(([name, tiles]) => (
        <div className="grp" key={name}>
          <div className="g-h">{name}</div>
          <div className="tiles">
            {tiles.slice(0, fit.cap).map(({ sku, available }) =>
              available === null ? (
                // Being counted here: the figure is the count's answer, so no figure is drawn.
                <div className="tile counting" key={sku.id} data-testid={`tile-${sku.code}`}>
                  <Swatch hex={sku.colourHex} name={sku.colourName} />
                  <span className="cn">{sku.colourName ?? sku.code}</span>
                  <span className="cnt">Counting</span>
                </div>
              ) : (
                // `stockLevel`, the rule the server's alerts and Office use: low, out and short tiles each look it.
                <div className={`tile ${stockLevel(available)}`} key={sku.id} data-testid={`tile-${sku.code}`}>
                  <Swatch hex={sku.colourHex} name={sku.colourName} />
                  <span className="cn">{sku.colourName ?? sku.code}</span>
                  <b className={wide(figure(available))}>{figure(available)}</b>
                </div>
              ),
            )}
            {tiles.length > fit.cap && <MoreTile n={tiles.length - fit.cap} />}
          </div>
        </div>
      ))}
      {offTheEnd > 0 && (
        <div className="grp">
          <div className="g-h">More</div>
          <div className="tiles">
            <MoreTile n={offTheEnd} />
          </div>
        </div>
      )}
    </section>
  );
}

/** A tile's worth of "+N more", for the SKUs a group had no room to show. */
function MoreTile({ n }: { n: number }) {
  return (
    <div className="tile more">
      <b>+{n}</b>
      <span>more</span>
    </div>
  );
}

export function LiveWall({
  initial,
  onUnauthorized,
  reload,
  EventSourceImpl,
  siteId,
}: {
  initial: WallSnapshotDto;
  onUnauthorized: () => void;
  reload: () => void;
  EventSourceImpl?: typeof EventSource;
  /** A signed-in person's preview names its site; a TV's is its device's. */
  siteId?: string;
}) {
  const { snapshot: s, receivedAt, status, showReconnecting } = useWallData(initial, onUnauthorized, EventSourceImpl, siteId);
  const tz = s.site.timezone;
  useNightlyReload(tz, reload);
  const fonts = useFontsLoaded();
  const skus = useMemo(() => new Map(s.skus.map((k) => [k.id, k])), [s.skus]);
  const sites = useMemo(() => new Map((s.sites ?? []).map((x) => [x.id, x])), [s.sites]);

  const picking = s.orders.filter((o) => o.status === 'picking').length;
  const scans = useMemo(() => s.recent.filter((e) => !(e.type === 'scan.rejected' && e.payload.mode === 'lookup')), [s.recent]);
  const orderFit = useFitRows<HTMLOListElement>(s.orders.length, [s.orders, fonts]);
  const scanFit = useFitRows<HTMLUListElement>(scans.length, [scans, fonts]);
  const alertFit = useFitRows<HTMLUListElement>(s.alerts.length, [s.alerts, fonts]);
  const inPlay = s.transfersOut.length + s.transfersIn.length;

  return (
    <div className={showReconnecting ? 'wall is-reconnecting' : 'wall'}>
      <div className="rail">
        <span>
          <b>{s.site.shortCode}</b> · {s.site.name.toUpperCase()} · {showReconnecting ? 'OFFLINE' : 'LIVE'}
        </span>
      </div>
      <div className="w-stage">
        <header className="w-head">
          <div className="w-mark">
            <i />
          </div>
          <div className="id">
            <div className="w-site">{s.site.name}</div>
            {!showReconnecting && <WallDate tz={tz} />}
          </div>
          {showReconnecting ? (
            <div className="w-reconnect" role="status">
              <Icon name="wifi-off" size={38} />
              Reconnecting… showing data from {shortTime(receivedAt, tz)}
            </div>
          ) : (
            <>
              <div className="grow" />
              <span className={status === 'live' ? 'w-conn' : 'w-conn warn'}>
                <i />
                {status === 'live' ? 'Live' : status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
              </span>
            </>
          )}
          <WallClock tz={tz} />
        </header>

        <div className="w-main">
          <section className="w-panel" aria-label="Ship today">
            <div className="w-ph ship-h">
              <span className="w-k">Ship today</span>
              <span className="grow" />
              <div className="totals">
                <div>
                  <b>{s.shippedToday}</b>
                  <span>shipped</span>
                </div>
                <div>
                  <b>{picking}</b>
                  <span>picking</span>
                </div>
              </div>
            </div>
            {s.orders.length ? (
              <ol className="orders" ref={orderFit.ref}>
                {s.orders.slice(0, orderFit.shown).map((o) => (
                  <OrderRow key={o.id} o={o} />
                ))}
                <MoreRow fit={orderFit} total={s.orders.length} />
              </ol>
            ) : (
              <div className="w-empty">Nothing to ship</div>
            )}
          </section>

          <section className="w-panel" aria-label="Live scans">
            <div className="w-ph">
              <span className="w-k">Live scans</span>
              <span className="grow" />
              <span className="note">newest first</span>
            </div>
            {scans.length ? (
              <ul className="feed" ref={scanFit.ref}>
                {scans.slice(0, scanFit.shown).map((e, i) => (
                  <ScanRow key={e.id} e={e} first={i === 0} skus={skus} tz={tz} />
                ))}
                <MoreRow fit={scanFit} total={scans.length} />
              </ul>
            ) : (
              <div className="w-empty">No scans yet today</div>
            )}
          </section>

          <div className="right">
            <section className="w-panel w-xfers" aria-label="Transfers">
              <div className="w-ph">
                <span className="w-k">Transfers</span>
                <span className="grow" />
                {inPlay > 0 && <span className="note">{inPlay} in play</span>}
              </div>
              <Transfers out={s.transfersOut} into={s.transfersIn} sites={sites} skus={skus} tz={tz} hereId={s.site.id} fonts={fonts} />
            </section>
            <section className="w-panel w-alerts" aria-label="Alerts">
              <div className="w-ph">
                <span className="w-k">Alerts</span>
              </div>
              {s.alerts.length ? (
                <ul className="alerts" ref={alertFit.ref}>
                  {s.alerts.slice(0, alertFit.shown).map((a, i) => (
                    <AlertRow key={`${a.kind}-${a.siteId}-${a.skuId ?? i}`} a={a} skus={skus} sites={sites} hereId={s.site.id} />
                  ))}
                  <MoreRow fit={alertFit} total={s.alerts.length} />
                </ul>
              ) : (
                <div className="w-empty">Nothing needs attention</div>
              )}
            </section>
          </div>
        </div>

        <AvailableNow s={s} fonts={fonts} />
      </div>
    </div>
  );
}
